import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Clock, ChevronRight, Sparkles, ShoppingBag, Headphones, Gift, Download } from "lucide-react";
import articles from "@/data/blogArticles";
import BlogCalendarOptin from "@/components/BlogCalendarOptin";
import BlogWorkbookCTA from "@/components/BlogWorkbookCTA";
import { Header } from "@/App";
import { blogOgImage } from "@/components/RouteSeo";
import { POST_DATES } from "@/pages/BlogIndex";
import LASTMOD from "@/data/sitemapLastmod.json";
import PUBLISHED from "@/data/blogPublished.json";

const ArticleList = () => {
  useEffect(() => {
    document.title = "Blog — Mind Body Spirit HYGIENE™ Tools | Free Resources & Guides";
  }, []);

  return (
    <div className="bg-[#efeae7] min-h-screen" data-testid="blog-page">
      <Header />

      <section className="pt-28 pb-16 md:pt-36 md:pb-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <BookOpen className="w-5 h-5 text-[#25a1c2]" />
              <p className="font-sans text-sm tracking-[0.2em] uppercase text-[#25a1c2] font-medium">Free Resources</p>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter text-[#042F2E] mb-5" data-testid="blog-title">
              The "I GET TO" Blog
            </h1>
            <p className="font-sans text-lg text-[#068fa2] max-w-2xl mx-auto">
              Free guides, scripts, and resources to help you set boundaries, manage anxiety, and redesign your mindset.
            </p>
          </div>

          <div className="space-y-6">
            {articles.filter(a => !a.hidden).map((article) => (
              <Link
                key={article.slug}
                to={`/blog/${article.slug}`}
                className="block group bg-white rounded-2xl border border-[#CCFBF1] overflow-hidden hover:shadow-lg transition-all hover:-translate-y-1"
                data-testid={`blog-card-${article.slug}`}
              >
                <div className={`bg-gradient-to-r ${article.heroColor} px-6 py-4 flex items-center justify-between`}>
                  <span className="text-white/70 text-xs font-bold uppercase tracking-widest">{article.category}</span>
                  {article.audio && (
                    <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border border-white/20" data-testid={`audio-badge-${article.slug}`}>
                      <Headphones className="w-3 h-3" />
                      Audio Essay
                    </span>
                  )}
                </div>
                <div className="p-6 md:p-8">
                  <h2 className="font-serif text-xl md:text-2xl text-[#042F2E] mb-2 group-hover:text-[#25a1c2] transition-colors">
                    {article.title}
                  </h2>
                  <p className="text-[#475569] text-sm leading-relaxed mb-4">{article.description}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-xs text-[#475569]">
                      <div className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        <span>{article.readTime}</span>
                      </div>
                      <span className="bg-[#F0F5F4] text-[#25a1c2] px-2 py-0.5 rounded-full font-medium">{article.product.name}</span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-[#25a1c2] group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
};

const ArticlePage = ({ slug }) => {
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    if (article) {
      // Meta Pixel — blog post view (safe no-op until pixel base loads)
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq("track", "ViewContent", {
          content_name: article.title,
          content_category: "blog",
          content_ids: [article.slug],
        });
      }
      window.scrollTo(0, 0);

      const url = `https://mindbodyspirithygienetools.com/blog/${article.slug}`;
      const ogImage = blogOgImage(article.slug);

      // JSON-LD structured data
      const existingLd = document.getElementById("article-jsonld");
      if (existingLd) existingLd.remove();

      const blogPosting = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: article.title,
        description: article.description,
        url,
        datePublished: PUBLISHED[article.slug] || POST_DATES[article.slug],
        dateModified: LASTMOD[`/blog/${article.slug}`] || PUBLISHED[article.slug],
        author: {
          "@type": "Person",
          name: "Temple Franklin",
          url: "https://mindbodyspirithygienetools.com/about",
        },
        publisher: {
          "@type": "Organization",
          name: "Mind Body Spirit HYGIENE™ Tools",
          url: "https://mindbodyspirithygienetools.com",
        },
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
        articleSection: article.category,
        image: ogImage,
      };

      const graph = [blogPosting];

      if (article.audio) {
        graph.push({
          "@context": "https://schema.org",
          "@type": "AudioObject",
          name: article.title,
          description: article.description,
          contentUrl: `https://mindbodyspirithygienetools.com${article.audio}`,
          encodingFormat: "audio/mp4",
          duration: article.readTime?.match(/(\d+)\s*min listen/i) ? `PT${article.readTime.match(/(\d+)\s*min listen/i)[1]}M` : "PT9M",
          inLanguage: "en-US",
          uploadDate: "2026-04-28",
          author: { "@type": "Person", name: "Temple Franklin" },
          publisher: { "@type": "Organization", name: "Mind Body Spirit HYGIENE™ Tools" },
          isAccessibleForFree: true,
        });
      }

      const ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.id = "article-jsonld";
      ld.text = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
      document.head.appendChild(ld);
    }

    return () => {
      const ld = document.getElementById("article-jsonld");
      if (ld) ld.remove();
    };
  }, [article]);

  if (!article) return <div className="min-h-screen flex items-center justify-center text-gray-500">Article not found.</div>;

  return (
    <div className="bg-[#efeae7] min-h-screen" data-testid={`article-${slug}`}>
      <Header />

      <article className={`max-w-3xl mx-auto px-6 pt-28 ${article.leadMagnet ? "pb-32" : "pb-16"}`}>
        <Link to="/blog" className="inline-block mb-4 text-[#25a1c2] hover:text-[#068fa2] text-sm font-medium" data-testid="back-to-blog">
          &larr; Back to Blog
        </Link>
        <div className={`bg-gradient-to-r ${article.heroColor} rounded-2xl p-8 md:p-10 mb-10`}>
          <span className="text-white/60 text-xs font-bold uppercase tracking-widest">{article.category} | {article.readTime}</span>
          <h1 className="font-serif text-3xl md:text-4xl text-white mt-3 leading-tight" data-testid="article-title">
            {article.title}
          </h1>
          <p className="text-white/70 text-sm mt-3">By Temple Franklin — Mind Body Spirit HYGIENE™ Tools</p>
        </div>

        <div className="mb-10">
          <p className="font-sans text-[#068fa2] text-lg leading-relaxed">{article.content.intro}</p>
        </div>

        {/* P0 — Box A: right after the opening scene (protected slug only) */}
        {article.slug === "the-not-so-perfect-storm" && (
          <BlogWorkbookCTA variant="A" slug={article.slug} />
        )}

        {article.audio && (
          <div className="mb-10 bg-gradient-to-br from-[#042F2E] via-[#25a1c2] to-[#068fa2] rounded-2xl p-6 md:p-8 border border-[#5EEAD4]/20" data-testid="article-audio-player">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 bg-[#5EEAD4]/20 rounded-full flex items-center justify-center">
                <Headphones className="w-5 h-5 text-[#5EEAD4]" />
              </div>
              <div>
                <p className="text-[#5EEAD4] text-xs font-bold uppercase tracking-widest">Listen to the Essay</p>
                <p className="text-white/70 text-xs">Read by Temple — in her real voice</p>
              </div>
            </div>
            <audio
              controls
              preload="metadata"
              className="w-full"
              data-testid="article-audio-element"
              style={{ colorScheme: "dark" }}
            >
              <source src={article.audio} type="audio/mp4" />
              Your browser does not support the audio element.
            </audio>
            <p className="text-white/60 text-xs mt-3 italic">
              Prefer to read? Full essay below. Want to keep it? Right-click &rarr; Save Audio As.
            </p>
          </div>
        )}

        {article.leadMagnet && (
          <Link
            to={article.leadMagnet.link}
            className="block group mb-10 bg-gradient-to-br from-[#FEF3C7] via-[#FDE68A] to-[#FCD34D] rounded-2xl p-6 md:p-7 border-2 border-[#F59E0B] hover:shadow-xl transition-all hover:-translate-y-0.5"
            data-testid="article-lead-magnet-inline"
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-[#92400E] rounded-full flex items-center justify-center">
                <Gift className="w-6 h-6 text-[#FDE68A]" />
              </div>
              <div className="flex-1">
                <p className="text-[#92400E] text-[10px] font-bold uppercase tracking-widest mb-1">FREE — While You Listen</p>
                <h3 className="font-serif text-xl md:text-2xl text-[#451A03] leading-tight mb-2">{article.leadMagnet.headline}</h3>
                <p className="text-[#78350F] text-sm leading-relaxed mb-3">{article.leadMagnet.subhead}</p>
                <span className="inline-flex items-center gap-2 bg-[#92400E] group-hover:bg-[#451A03] text-white rounded-full px-5 py-2.5 font-bold text-sm transition-all">
                  <Download className="w-4 h-4" />
                  {article.leadMagnet.cta}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </div>
          </Link>
        )}

        <div className="space-y-10 mb-12">
          {article.content.body.map((section, i) => (
            <div key={i}>
              {/* P0 — Box B: emotional turning point, before the resolution (protected slug only) */}
              {article.slug === "the-not-so-perfect-storm" && section.heading === "What I have learned" && (
                <BlogWorkbookCTA variant="B" slug={article.slug} />
              )}
              <h2 className="font-serif text-2xl text-[#042F2E] mb-4">{section.heading}</h2>
              {section.situation && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                  <p className="text-red-400 text-xs font-bold uppercase tracking-widest mb-1">The Situation</p>
                  <p className="text-red-700 text-sm italic">{section.situation}</p>
                </div>
              )}
              {section.script && (
                <div className="bg-[#F0FDF4] border-2 border-[#25a1c2]/20 rounded-xl p-4 mb-4">
                  <p className="text-[#25a1c2] text-xs font-bold uppercase tracking-widest mb-1">What to Say</p>
                  <p className="text-[#068fa2] font-medium">{section.script}</p>
                </div>
              )}
              {section.why && (
                <p className="text-[#475569] text-sm italic mb-4">{section.why}</p>
              )}
              <p className="font-sans text-[#068fa2] leading-relaxed whitespace-pre-line">{section.text}</p>
            </div>
          ))}
        </div>

        {/* In-body internal link to /book (SEO) — on every post, separate from gold CTAs */}
        <p className="font-sans text-[#068fa2] leading-relaxed mb-12" data-testid="article-book-inline-link">
          These are the kinds of tools I teach in{" "}
          <Link
            to="/book"
            className="text-[#042F2E] font-semibold underline decoration-[#e8b04b] decoration-2 underline-offset-2 hover:text-[#068fa2] transition-colors"
          >
            The "I Get To" Book — real tools for real women in the messy middle
          </Link>
          .
        </p>

        <div className="bg-[#F0F5F4] border border-[#CCFBF1] rounded-2xl p-8 mb-10">
          <p className="font-sans text-[#068fa2] leading-relaxed">{article.content.closing}</p>
        </div>

        {/* ════════ More from the Blog — Internal Linking for SEO ════════ */}
        {(() => {
          const related = articles
            .filter((a) => a.slug !== article.slug && !a.hidden)
            .sort((a, b) => {
              // Same category first
              if (a.category === article.category && b.category !== article.category) return -1;
              if (b.category === article.category && a.category !== article.category) return 1;
              return 0;
            })
            .slice(0, 3);
          return related.length > 0 ? (
            <div className="mb-10" data-testid="article-related-posts">
              <div className="flex items-center gap-2 mb-5">
                <BookOpen className="w-5 h-5 text-[#25a1c2]" />
                <h3 className="font-serif text-xl md:text-2xl text-[#042F2E]">More from the Blog</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/blog/${r.slug}`}
                    className="block group bg-white rounded-2xl border border-[#CCFBF1] overflow-hidden hover:shadow-lg transition-all hover:-translate-y-1"
                    data-testid={`related-post-${r.slug}`}
                  >
                    <div className={`bg-gradient-to-r ${r.heroColor} px-4 py-2.5`}>
                      <span className="text-white/80 text-[10px] font-bold uppercase tracking-widest">{r.category}</span>
                    </div>
                    <div className="p-4">
                      <h4 className="font-serif text-base text-[#042F2E] leading-snug mb-2 group-hover:text-[#25a1c2] transition-colors line-clamp-3">
                        {r.title}
                      </h4>
                      <p className="font-sans text-xs text-[#068fa2] leading-relaxed line-clamp-2">{r.description}</p>
                      <div className="flex items-center gap-1.5 mt-3 text-[#25a1c2] text-xs font-medium">
                        Read more <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="text-center mt-5">
                <Link
                  to="/blog"
                  className="inline-flex items-center gap-1.5 text-[#25a1c2] hover:text-[#068fa2] text-sm font-medium transition-colors"
                  data-testid="related-see-all"
                >
                  See all articles <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          ) : null;
        })()}

        {article.leadMagnet ? (
          <div className="bg-gradient-to-br from-[#92400E] via-[#B45309] to-[#D97706] rounded-2xl p-8 md:p-10 text-center" data-testid="article-lead-magnet-cta">
            <Gift className="w-10 h-10 text-[#FEF3C7] mx-auto mb-4" />
            <h3 className="font-serif text-2xl md:text-3xl text-white mb-3">{article.leadMagnet.bottomHeadline || article.leadMagnet.headline}</h3>
            <p className="text-[#FEF3C7]/90 text-sm md:text-base mb-6 max-w-md mx-auto leading-relaxed">
              {article.leadMagnet.bottomSubhead || article.leadMagnet.subhead}
            </p>
            <Link
              to={article.leadMagnet.link}
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-[#FEF3C7] text-[#92400E] rounded-full px-8 py-4 font-bold text-base transition-all hover:-translate-y-0.5 shadow-lg"
              data-testid="article-lead-magnet-bottom-btn"
            >
              <Download className="w-5 h-5" />
              {article.leadMagnet.cta}
            </Link>
            <p className="text-[#FEF3C7]/70 text-xs mt-4 italic">100% free · No credit card · Instant printable</p>
          </div>
        ) : (
          <div className="bg-[#042F2E] rounded-2xl p-8 md:p-10 text-center" data-testid="article-product-cta">
            <Sparkles className="w-8 h-8 text-[#5EEAD4] mx-auto mb-4" />
            <h3 className="font-serif text-2xl text-white mb-3">Want the Complete Toolkit?</h3>
            <p className="text-white/70 text-sm mb-6 max-w-md mx-auto">
              This article is a free sample. <strong className="text-white">{article.product.name}</strong> gives you the full system — printable, portable, yours to keep.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/book"
                onClick={() => {
                  if (typeof window !== "undefined" && typeof window.gtag === "function") {
                    window.gtag("event", "book_pdf_click", { from: `blog-${article.slug}`, price: 9.99 });
                  }
                }}
                className="inline-flex items-center justify-center gap-2 rounded-full px-8 py-3.5 font-bold transition-all hover:-translate-y-0.5 shadow-lg"
                style={{ background: "#F4C36A", color: "#1B6B6E" }}
                data-testid="article-buy-btn"
              >
                <ShoppingBag className="w-4 h-4" />
                Get the full book — instant PDF · $9.99
              </Link>
              <Link
                to="/book#free-chapter"
                onClick={() => {
                  if (typeof window !== "undefined" && typeof window.gtag === "function") {
                    window.gtag("event", "book_sample_click", { from: `blog-${article.slug}` });
                  }
                }}
                className="inline-flex items-center justify-center gap-2 border-2 border-white/40 hover:border-white text-white rounded-full px-8 py-3.5 font-medium transition-all hover:-translate-y-0.5"
                data-testid="article-free-taste-btn"
              >
                <Sparkles className="w-4 h-4" />
                Read a free taste
              </Link>
            </div>
            <p className="mt-5">
              <Link
                to="/build-your-own"
                className="font-sans text-sm font-semibold text-[#5EEAD4] underline underline-offset-4 hover:text-white transition-colors"
                data-testid="article-course-link"
              >
                Or start the 23-lesson course — $33
              </Link>
            </p>
            <p className="text-white/50 text-xs mt-4 italic">
              Prefer paperback? <Link to="/book" className="underline underline-offset-2 hover:text-white/80" data-testid="article-paperback-link">See print options →</Link>
            </p>
          </div>
        )}

        <div className="text-center mt-10 pt-10 border-t border-[#CCFBF1]">
          {!article.hideCalendarOptin && (
            <BlogCalendarOptin
              articleSlug={article.slug}
              headline={article.calendarOptin?.headline}
              body={article.calendarOptin?.body}
              microcopy={article.calendarOptin?.microcopy}
            />
          )}
        </div>
      </article>

      {article.leadMagnet && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-gradient-to-r from-[#92400E] via-[#B45309] to-[#D97706] border-t-2 border-[#F59E0B] shadow-2xl" data-testid="article-sticky-lead-magnet">
          <Link to={article.leadMagnet.link} className="block group">
            <div className="max-w-4xl mx-auto px-4 py-3 md:py-4 flex items-center gap-3 md:gap-4">
              <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 bg-white rounded-full flex items-center justify-center">
                <Gift className="w-5 h-5 md:w-6 md:h-6 text-[#92400E]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[#FEF3C7] text-[10px] md:text-xs font-bold uppercase tracking-widest leading-tight">FREE — Download Now</p>
                <p className="text-white text-sm md:text-base font-medium leading-tight truncate">{article.leadMagnet.stickyText || article.leadMagnet.headline}</p>
              </div>
              <span className="flex-shrink-0 inline-flex items-center gap-1.5 bg-white group-hover:bg-[#FEF3C7] text-[#92400E] rounded-full px-4 py-2 md:px-6 md:py-2.5 font-bold text-xs md:text-sm transition-all whitespace-nowrap">
                Get It Free
                <ArrowRight className="w-3.5 h-3.5 md:w-4 md:h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </div>
          </Link>
        </div>
      )}
    </div>
  );
};

export { ArticleList, ArticlePage, articles };
